package com.videoanalyzer.web.components.analysis

import androidx.compose.runtime.Composable
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * Shared utility components and functions for analysis cards.
 */

// ============================================================
// Utility Functions
// ============================================================

fun formatNumber(num: Long): String = when {
    num >= 1_000_000 -> "${(num / 1_000_000.0).asDynamic().toFixed(1)}M"
    num >= 1_000 -> "${(num / 1_000.0).asDynamic().toFixed(1)}K"
    else -> num.toString()
}

fun getScoreColor(score: Int): String = when {
    score >= 80 -> "#22C55E"
    score >= 60 -> "#84CC16"
    score >= 40 -> "#F59E0B"
    else -> "#EF4444"
}

fun adjustColor(color: String): String = when (color) {
    "#22C55E" -> "#16A34A"
    "#84CC16" -> "#65A30D"
    "#F59E0B" -> "#D97706"
    "#EF4444" -> "#DC2626"
    else -> color
}

/**
 * Format rating text for display.
 * Converts "very_good" to "Very Good", "above_average_for_length" to "Above Average"
 */
fun formatRating(rating: String): String {
    val withSpaces = rating.replace("_", " ")
    val cleaned = withSpaces
        .replace(Regex("\\s+for\\s+.*$", RegexOption.IGNORE_CASE), "")
        .replace(Regex("\\s+in\\s+.*$", RegexOption.IGNORE_CASE), "")
        .trim()
    return cleaned.split(" ")
        .filter { it.isNotBlank() }
        .joinToString(" ") { word ->
            word.lowercase().replaceFirstChar { it.uppercase() }
        }
}

// ============================================================
// Reusable Card Components
// ============================================================

@Composable
fun AnalysisSectionCard(
    icon: String,
    title: String,
    score: Int?,
    gradientStart: String,
    gradientEnd: String,
    content: @Composable () -> Unit
) {
    Div({
        classes("card", "fade-in-up")
        style {
            height(100.percent)
            property("border-top", "2px solid $gradientStart")
        }
    }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.SpaceBetween)
                marginBottom(16.px)
            }
        }) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(10.px)
                }
            }) {
                Div({
                    style {
                        width(32.px)
                        height(32.px)
                        borderRadius(8.px)
                        backgroundColor(Color("rgba(255, 255, 255, 0.05)"))
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        justifyContent(JustifyContent.Center)
                        fontSize(16.px)
                    }
                }) { Text(icon) }

                H4({
                    style {
                        fontFamily("'Sora', sans-serif")
                        fontSize(15.px)
                        fontWeight(600)
                        color(Color("#FAFAFA"))
                    }
                }) { Text(title) }
            }

            score?.let { ScoreBadge(it) }
        }

        content()
    }
}

@Composable
fun ScoreBadge(score: Int) {
    val color = getScoreColor(score)
    Div({
        style {
            backgroundColor(Color("${color}20"))
            color(Color(color))
            padding(3.px, 8.px)
            borderRadius(4.px)
            fontSize(12.px)
            fontWeight(600)
            fontFamily("'SF Mono', Monaco, monospace")
        }
    }) { Text("$score") }
}

@Composable
fun StatItem(label: String, value: String) {
    Div({
        style {
            textAlign("center")
            backgroundColor(Color("rgba(255, 255, 255, 0.03)"))
            padding(10.px, 8.px)
            borderRadius(8.px)
            property("border", "1px solid rgba(255, 255, 255, 0.05)")
        }
    }) {
        Div({
            style {
                fontFamily("'SF Mono', Monaco, monospace")
                fontSize(16.px)
                fontWeight(600)
                color(Color("#FAFAFA"))
            }
        }) { Text(value) }
        Div({
            style {
                fontSize(10.px)
                color(Color("#71717A"))
                marginTop(2.px)
            }
        }) { Text(label) }
    }
}

@Composable
fun RatingBadge(label: String, rating: String) {
    val normalizedRating = rating.lowercase().replace("_", " ")

    val color = when {
        normalizedRating.contains("excellent") || normalizedRating.contains("very good") ||
        normalizedRating.contains("very high") || normalizedRating.contains("strong") ||
        normalizedRating.contains("trending") || normalizedRating.contains("large") ||
        normalizedRating.contains("mega") || normalizedRating.contains("authority") ||
        normalizedRating.contains("very consistent") || normalizedRating.contains("high") -> "#22C55E"

        normalizedRating.contains("good") || normalizedRating.contains("clear") ||
        normalizedRating.contains("established") || normalizedRating.contains("consistent") ||
        normalizedRating.contains("growing") || normalizedRating.contains("above") -> "#84CC16"

        normalizedRating.contains("average") || normalizedRating.contains("moderate") ||
        normalizedRating.contains("building") || normalizedRating.contains("medium") -> "#F59E0B"

        normalizedRating.contains("poor") || normalizedRating.contains("low") ||
        normalizedRating.contains("weak") || normalizedRating.contains("bad") -> "#EF4444"

        else -> "#A1A1AA"
    }

    val displayRating = formatRating(rating)

    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(4.px)
            backgroundColor(Color("#27272A"))
            padding(4.px, 8.px)
            borderRadius(4.px)
        }
    }) {
        Span({
            style {
                fontSize(10.px)
                color(Color("#71717A"))
            }
        }) { Text(label) }
        Span({
            style {
                fontSize(11.px)
                fontWeight(600)
                color(Color(color))
            }
        }) { Text(displayRating) }
    }
}

@Composable
fun FeatureCheck(label: String, isPresent: Boolean) {
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(4.px)
        }
    }) {
        Span({ style { fontSize(12.px) } }) { Text(if (isPresent) "✓" else "✗") }
        Span({
            style {
                fontSize(11.px)
                color(Color(if (isPresent) "#22C55E" else "#71717A"))
            }
        }) { Text(label) }
    }
}

@Composable
fun InsightText(text: String) {
    Div({
        style {
            backgroundColor(Color("#27272A"))
            padding(8.px, 10.px)
            borderRadius(6.px)
            marginTop(6.px)
        }
    }) {
        Span({
            style {
                fontSize(12.px)
                color(Color("#A1A1AA"))
                lineHeight("1.5")
            }
        }) { Text(text) }
    }
}

@Composable
fun SuggestionsList(suggestions: List<String>) {
    if (suggestions.isEmpty()) return

    Div({ style { marginTop(12.px) } }) {
        suggestions.take(2).forEach { suggestion ->
            MiniSuggestionItem(suggestion)
        }
    }
}

@Composable
fun MiniSuggestionItem(text: String) {
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.FlexStart)
            gap(6.px)
            padding(4.px, 0.px)
        }
    }) {
        Span({
            style {
                color(Color("#0EA5E9"))
                fontSize(11.px)
            }
        }) { Text("→") }
        Span({
            style {
                color(Color("#A1A1AA"))
                fontSize(11.px)
                lineHeight("1.4")
            }
        }) { Text(text) }
    }
}

@Composable
fun StrategyItem(icon: String, title: String, content: String) {
    Div({
        style {
            backgroundColor(Color("#27272A"))
            padding(12.px)
            borderRadius(6.px)
        }
    }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(6.px)
                marginBottom(6.px)
            }
        }) {
            Span({ style { fontSize(14.px) } }) { Text(icon) }
            Span({
                style {
                    fontSize(12.px)
                    fontWeight(600)
                    color(Color("#FAFAFA"))
                }
            }) { Text(title) }
        }
        Span({
            style {
                fontSize(12.px)
                color(Color("#A1A1AA"))
                lineHeight("1.5")
            }
        }) { Text(content) }
    }
}

@Composable
fun TipsList(title: String, tips: List<String>) {
    Div({
        style {
            backgroundColor(Color("#27272A"))
            padding(10.px)
            borderRadius(6.px)
        }
    }) {
        Span({
            style {
                fontSize(12.px)
                fontWeight(600)
                color(Color("#FAFAFA"))
                display(DisplayStyle.Block)
                marginBottom(8.px)
            }
        }) { Text(title) }

        tips.take(3).forEach { tip ->
            MiniSuggestionItem(tip)
        }
    }
}
